/**
 * The calendar in words (FR-E7, A-09).
 *
 * `GET /periods/obligations` answers in codes and paise: a return type, a state, days as a signed
 * number, a late-fee block with its own unverified flag, and the basis on which nil was decided.
 * A person reads a sentence — "GSTR-3B for 08-2026 — due 2026-09-20, 18 days late, late fee so
 * far ₹900.00 (rate not yet verified)" — and this is where the codes become that sentence, so the
 * screen renders and never decides (ADR-0014) and no slug reaches a person (UI test plan class C).
 */

import type { ObligationRecord } from './api'
import { periodWords } from './periodWords'
import { RETURN_NAMES, STATE_LABELS } from './returnWords'

/**
 * `GSTR1` → `GSTR-1`, as the practice says it. One map with `returnWords.ts`'s `RETURN_NAMES`
 * (which carries `GSTR1A`, B-07), so the rail, the header and the calendar cannot disagree.
 */
export const RETURN_WORDS: Record<string, string> = RETURN_NAMES

export function returnWords(returnType: string): string {
  return RETURN_WORDS[returnType] ?? returnType
}

/** The period's state, including the one the calendar alone can show. */
export function stateWords(state: string): string {
  if (state === 'not_opened') return 'Not opened'
  // FR-E16: a PMT-06 deposit — ProGST works out the figure and does not record the payment.
  if (state === 'not_tracked') return 'Payment not recorded in ProGST'
  return STATE_LABELS[state] ?? state
}

/**
 * Filed and locked are listed and say nothing more. So, once the date has passed, are the two
 * QRMP obligations nothing can be done about late (FR-E16) — the service's `is_quiet`.
 */
export function isQuiet(o: Pick<ObligationRecord, 'state'> & Partial<ObligationRecord>): boolean {
  if (o.state === 'filed' || o.state === 'locked') return true
  return (o.return_type === 'IFF' || o.return_type === 'PMT06') && (o.days ?? 0) > 0
}

/** Why a quiet QRMP row is quiet, in words. */
function closedWords(o: ObligationRecord): string | null {
  if (o.return_type === 'IFF') return "the IFF's window has closed; its invoices go in the quarter's GSTR-1"
  if (o.return_type === 'PMT06') return 'the deposit date has passed; ProGST does not record the payment'
  return null
}

/** Days as the service signs them: negative = still to come, positive = late. */
export function dueWords(days: number): string {
  if (days > 1) return `${days} days late`
  if (days === 1) return '1 day late'
  if (days === 0) return 'due today'
  if (days === -1) return 'due tomorrow'
  return `due in ${-days} days`
}

/** Indian digit grouping, paise in — the same reading as the Returns screen. */
export function rupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Why the fee was computed at the rate it was (`calendar.NIL_BASES`), in words. */
export const NIL_BASIS_WORDS: Record<string, string> = {
  nil_register: 'nil return — the register read for this month has no rows',
  no_period: 'liable rate — nothing opened for this month yet',
  snapshot: 'a return filed before ProGST held these books',
  no_documents: 'liable rate assumed — no sales document read for this month yet',
  rows_pending: 'liable rate — rows are still waiting on a decision',
  supplies: 'liable rate — the draft books outward supplies',
  inward_credit: 'liable rate — a GSTR-2B is imported, so there is credit to claim',
}

export function nilBasisWords(basis: string): string {
  return NIL_BASIS_WORDS[basis] ?? 'rate basis not stated'
}

/**
 * The late-fee clause, or nothing where none applies. D-14's flag is a word, never a bare number
 * pretending to be checked; a fee the service could not compute says why.
 */
export function lateFeeWords(o: Pick<ObligationRecord, 'late_fee' | 'is_nil'>): string | null {
  const fee = o.late_fee
  if (!fee) return null
  if (fee['state'] === 'not_computed') {
    return `late fee not computed (${String(fee['reason'] ?? 'no parameters')})`
  }
  const accrued = Number(fee['accrued_paise'] ?? 0)
  const parts = [`late fee so far ₹${rupees(accrued)}`]
  const notes: string[] = []
  if (o.is_nil) notes.push('nil rate')
  if (fee['capped'] === true) notes.push('at the cap')
  if (fee['parameters_unverified'] === true) notes.push('rate not yet verified')
  if (notes.length) parts.push(`(${notes.join(', ')})`)
  return parts.join(' ')
}

/** The one sentence a row is. */
export function obligationSentence(o: ObligationRecord): string {
  const optional = o.return_type === 'IFF' ? ' (optional)' : ''
  const head = `${returnWords(o.return_type)}${optional} for ${periodWords(o.period)} — due ${o.due_date}`
  if (isQuiet(o)) return `${head}, ${closedWords(o) ?? stateWords(o.state).toLowerCase()}`
  const fee = lateFeeWords(o)
  return fee ? `${head}, ${dueWords(o.days)}, ${fee}` : `${head}, ${dueWords(o.days)}`
}

export interface ClientGroup {
  client_id: string
  client_name: string
  rows: ObligationRecord[]
}

/** The same rows, by client, in the order the service sent them (most urgent first). */
export function groupByClient(rows: ObligationRecord[]): ClientGroup[] {
  const groups = new Map<string, ClientGroup>()
  for (const row of rows) {
    let group = groups.get(row.client_id)
    if (!group) {
      group = { client_id: row.client_id, client_name: row.client_name, rows: [] }
      groups.set(row.client_id, group)
    }
    group.rows.push(row)
  }
  return [...groups.values()]
}

export interface Summary {
  overdue: number
  dueThisWeek: number
}

/** What the Overview's line says: unfiled past the date, and unfiled due within seven days. */
export function summarise(rows: ObligationRecord[]): Summary {
  let overdue = 0
  let dueThisWeek = 0
  for (const row of rows) {
    if (isQuiet(row)) continue
    if (row.days > 0) overdue += 1
    else if (row.days >= -7) dueThisWeek += 1
  }
  return { overdue, dueThisWeek }
}

export function summaryWords(s: Summary): string {
  const overdue = s.overdue === 1 ? '1 return overdue' : `${s.overdue} returns overdue`
  const week = s.dueThisWeek === 1 ? '1 due this week' : `${s.dueThisWeek} due this week`
  return `${overdue} · ${week}`
}
